'use client';
import { useEffect, useRef, useState } from 'react';
import { GoogleAuthProvider, signInWithCredential } from 'firebase/auth';
import { auth } from '../config/firebaseConfig';
import authService from '../services/authService';
import useUserStore from '../store/userStore';

function OneTapLogin() {
  const isLoggedIn = useUserStore(state => state.isLoggedIn);
  const user = useUserStore(state => state.user);
  const setUserManually = useUserStore(state => state.setUserManually);
  const fetchUserProfile = useUserStore(state => state.fetchUserProfile);

  const isMounted = useRef(true);
  const isInitialized = useRef(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  // Проверяем, создан ли пользователь в бэкенде
  const checkUserCreated = async (retryCount = 0, maxRetries = 5) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return { exists: false, retryable: false };
      }

      const firebaseToken = await currentUser.getIdToken(true);
      const timestamp = new Date().getTime();
      const checkUrl = `${process.env.NEXT_PUBLIC_BACKEND_URL}/check_user_created?_t=${timestamp}`;

      const response = await fetch(checkUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${firebaseToken}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-store, no-cache, must-revalidate, private'
        },
      });

      if (!response.ok) {
        const retryableStatus = [408, 429, 500, 502, 503, 504];
        const isRetryable = retryableStatus.includes(response.status);
        return { exists: false, retryable: isRetryable };
      }

      const data = await response.json();
      return { exists: !!data.exists, retryable: true };
    } catch (err) {
      console.error('[OneTapLogin] Error checking user:', err);
      return { exists: false, retryable: true };
    }
  };

  // Проверяем пользователя с экспоненциальной задержкой
  const checkUserWithRetry = async (isNewUser = false) => {
    let retryCount = 0;
    const maxRetries = isNewUser ? 10 : 5;
    let delayMs = 1000;

    let checkResult = await checkUserCreated(retryCount, maxRetries);
    if (checkResult.exists) return true;

    while (retryCount < maxRetries && checkResult.retryable && isMounted.current) {
      retryCount++;
      const jitter = 0.8 + (Math.random() * 0.4);
      delayMs = Math.min(delayMs * 1.5 * jitter, 8000);
      await new Promise(resolve => setTimeout(resolve, delayMs));
      if (!isMounted.current) return false;
      checkResult = await checkUserCreated(retryCount, maxRetries);
      if (checkResult.exists) return true;
    }
    return false;
  };

  // Обработчик учетных данных Google
  const handleCredential = async (response) => {
    if (!response?.credential || !isMounted.current) return;
    if (isAuthenticating) return;

    setIsAuthenticating(true);
    try {
      localStorage.setItem('auth_action', 'login');
      sessionStorage.removeItem('backend_verified');

      const idToken = response.credential;
      const googleCredential = GoogleAuthProvider.credential(idToken);
      const userCredential = await signInWithCredential(auth, googleCredential);

      if (userCredential?.user && isMounted.current) {
        let isNewUser = false;
        if (userCredential.user && userCredential.user.metadata) {
          const { creationTime, lastSignInTime } = userCredential.user.metadata;
          isNewUser = creationTime === lastSignInTime;
          localStorage.setItem('is_new_user', isNewUser ? 'true' : 'false');
        }

        const userCreated = await checkUserWithRetry(isNewUser);
        if (userCreated && isMounted.current) {
          sessionStorage.setItem('backend_verified', 'true');
          localStorage.removeItem('auth_action');

          try {
            const authResult = await authService.exchangeFirebaseToken();
            if (authResult?.user) {
              setUserManually(authResult.user);
              await fetchUserProfile(true);

              try {
                if (typeof window !== 'undefined' && isMounted.current) {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    event: isNewUser ? 'user_registration' : 'user_login',
                    userID: userCredential.user.uid,
                    userEmail: userCredential.user.email,
                    userSource: userCredential.user?.providerData?.[0]?.providerId || 'google.com'
                  });
                }
              } catch (analyticsError) {
                console.error('[OneTapLogin] Analytics error:', analyticsError);
              }

              performUIRefresh();
            } else {
              console.error('[OneTapLogin] Token exchange failed or returned no user');
              handleAuthError(new Error('Token exchange failed'));
            }
          } catch (error) {
            console.error('[OneTapLogin] Error during token exchange:', error);
            handleAuthError(error);
          }
        } else {
          console.error('[OneTapLogin] Failed to find user in database after retries');
          handleAuthError(new Error('User creation failed'));
        }
      }
    } catch (error) {
      console.error('[OneTapLogin] Authentication error:', error);
      handleAuthError(error);
    } finally {
      setIsAuthenticating(false);
    }
  };

  // Обработка ошибок аутентификации
  const handleAuthError = async (error) => {
    console.error('[OneTapLogin] Auth error:', error.message);
    try {
      await auth.signOut();
      localStorage.removeItem('auth_action');

      if (typeof window !== 'undefined' && isMounted.current) {
        if (window.location.pathname.includes('login')) {
          const newUrl = window.location.pathname;
          window.history.replaceState(null, '', newUrl);
        } else {
          const currentPath = window.location.pathname + window.location.search;
          window.location.href = `/login?next=${encodeURIComponent(currentPath)}`;
        }
      }
    } catch (e) {
      console.error('[OneTapLogin] Error during auth error handling:', e);
      if (typeof window !== 'undefined') window.location.reload();
    }
  };

  // Обновление UI после входа
  const performUIRefresh = () => {
    if (typeof window === 'undefined' || !isMounted.current) return;
    try {
      if (window.next && typeof window.next.router?.refresh === 'function') {
        window.next.router.refresh();
        return;
      }
      window.dispatchEvent(new CustomEvent('login-success'));
      const currentUrl = window.location.pathname;
      if (currentUrl.includes('/login')) {
        const urlParams = new URLSearchParams(window.location.search);
        const nextUrl = urlParams.get('next') || '/';
        setTimeout(() => { window.location.href = nextUrl; }, 300);
        return;
      }
      setTimeout(() => { window.location.reload(); }, 500);
    } catch (refreshError) {
      console.error('[OneTapLogin] Error during UI refresh:', refreshError);
      setTimeout(() => { window.location.reload(); }, 500);
    }
  };

  // Проверка, был ли недавний logout
  const wasRecentLogout = () => {
    const logoutTime = localStorage.getItem('last_logout_time');
    if (!logoutTime) return false;
    
    const timeSinceLogout = Date.now() - parseInt(logoutTime);
    const LOGOUT_COOLDOWN = 10000; // 10 секунд
    
    return timeSinceLogout < LOGOUT_COOLDOWN;
  };

  // Загрузка Google API
  const loadGoogleApi = () => {
    if (window.google?.accounts?.id) {
      initGoogleOneTap();
      return;
    }
    if (document.getElementById('google-one-tap-script')) return;
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.id = 'google-one-tap-script';
    script.onload = () => { if (isMounted.current) initGoogleOneTap(); };
    script.onerror = (error) => { console.error('[OneTapLogin] Error loading Google API script:', error); };
    document.head.appendChild(script);
  };

  // Инициализация Google One Tap
  const initGoogleOneTap = () => {
    if (!window.google?.accounts?.id || !isMounted.current) return;
    if (window.location.pathname.includes('/login')) return;

    const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ||
      '911253301722-bcl1r9lkh81nngrpbgqcr9d82t9afj06.apps.googleusercontent.com';

    try {
      try { window.google.accounts.id.cancel(); } catch (e) {/* ignore */}
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleCredential,
        auto_select: true,
        cancel_on_tap_outside: true
      });
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          /* silent */
        }
      });
    } catch (e) {
      console.error('[OneTapLogin] Error initializing Google One Tap:', e);
    }
  };

  useEffect(() => {
    isMounted.current = true;
    setIsAuthenticating(false);

    if (isInitialized.current || typeof window === 'undefined') return;
    if (isLoggedIn || user || auth.currentUser) return;
    
    // ИСПРАВЛЕНИЕ: Проверяем флаги logout и недавний logout
    const manualLogout = localStorage.getItem('manual_logout') === 'true';
    const forceLogout = localStorage.getItem('force_logout') === 'true';
    const recentLogout = wasRecentLogout();
    
    if (manualLogout || forceLogout || recentLogout) {
      console.log('[OneTapLogin] Skipping OneTap due to recent logout');
      
      // Очищаем флаги через некоторое время
      setTimeout(() => {
        localStorage.removeItem('manual_logout');
        localStorage.removeItem('force_logout');
      }, 5000); // 5 секунд задержка
      
      return;
    }

    isInitialized.current = true;
    const timeoutId = setTimeout(loadGoogleApi, 1000);

    return () => {
      isMounted.current = false;
      clearTimeout(timeoutId);
      if (window.google?.accounts?.id) {
        try { window.google.accounts.id.cancel(); } catch (e) {/* ignore */}
      }
    };
  }, [isLoggedIn, user, setUserManually, fetchUserProfile]);

  return null;
}

export default OneTapLogin;